import React, { lazy, useEffect, useState } from 'react';
import Components from 'src/components.js';
import Header from 'components/global/header';
/* eslint-disable react/prefer-stateless-function */
import { graphql } from 'gatsby';
import Helmet from 'react-helmet';

const LazyFooter = lazy(() => import('components/global/footer'));

const DelayedComponent = ({ children, delay }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  return show ? children : null;
};
class StoryblokEntry extends React.Component {
  render() {
    const {
      story,
      header,
      footer,
      breadcrumb,
      showrooms,
      cookie,
      language,
      isFurnishingCategory,
      productRegex,
      search,
      general_config,
      ...rest
    } = this.props.pageContext;

    const cookieInformationLangCodes = {
      se: 'SV',
      dk: 'DA',
      en: 'EN',
      no: 'NB',
    };

    const { data } = this.props;
    const component = story.content.component;

    if (component === 'page_start' || component === 'page_scroll') {
      return (
        <div>
          <Header
            blok={header.content}
            search={search?.content}
            slug={header.full_slug}
            showrooms={showrooms}
          />
          <div id="main">
            {React.createElement(Components(component), {
              key: story.content._uid,
              blok: story.content,
              name: story.name,
              tags: story.tag_list,
              full_slug: story.full_slug,
              footer,
              showrooms,
              generalConfig: general_config?.content,
              ...rest,
            })}
          </div>
          {component === 'page_start' && (
            <DelayedComponent delay={1000}>
              <LazyFooter blok={footer.content} full_slug={footer.full_slug} />
            </DelayedComponent>
          )}
          <Helmet defer={false}>
            <script
              id="CookieConsent"
              src="https://policy.app.cookieinformation.com/uc.js"
              data-culture={cookieInformationLangCodes[language]}
              data-gcm-version="2.0"
              type="text/javascript"
            />
          </Helmet>
        </div>
      );
    }

    return (
      <div>
        <Header
          crumbs={breadcrumb.crumbs}
          blok={header.content}
          slug={header.full_slug}
          showrooms={showrooms}
          search={search?.content}
        />
        <div id="main">
          {React.createElement(Components(component), {
            key: story.content._uid,
            blok: component === 'furnishing' ? null : story.content, // Does not send blok if using custom GraphQL data
            name: story.name,
            tags: story.tag_list,
            full_slug: story.full_slug,
            data,
            showrooms:
              component === 'page_showrooms' ||
              component === 'product' ||
              component === 'page'
                ? showrooms
                : null,
            generalConfig: general_config?.content,
            ...rest,
          })}
        </div>
        <DelayedComponent delay={1000}>
        <LazyFooter blok={footer.content} full_slug={footer.full_slug} />
        </DelayedComponent>
        <Helmet defer={false}>
          <script
            id="CookieConsent"
            src="https://policy.app.cookieinformation.com/uc.js"
            data-culture={cookieInformationLangCodes[language]}
            data-gcm-version="2.0"
            type="text/javascript"
          />
        </Helmet>
      </div>
    );
  }
}

export const query = graphql`
  fragment Options on StoryblokEntry {
    options: field_options_array {
      media
      name
      price
      variant_id
      attributes {
        metall
        size
        type
      }
    }
  }
  query PageSpecificDataQueries(
    $isFurnishingProduct: Boolean = false
    $isFurnishingCategory: Boolean = false
    $originalPath: String
    $languageRegex: String
    $productRegex: String
  ) {
    furnishings: allStoryblokEntry(
      filter: {
        field_component: { eq: "furnishing" }
        full_slug: { regex: $productRegex }
      }
      sort: { order: DESC, fields: sort_by_date }
    ) @include(if: $isFurnishingCategory) {
      edges {
        node {
          name
          slug
          fullSlug: full_slug
          field_component
          sort_by_date
          shortDescription: field_short_description_string
          images: field_images_array {
            name
            src: filename
          }
          price: field_price_string
          thumbnail: field_thumbnail_object {
            name
            src: filename
          }
          field_color_string
          field_material_string
          field_brand_string
          ...Options
        }
      }
    }
    clonedFurnishings: allStoryblokEntry(
      filter: {
        field_component: { eq: "furnishing" }
        field_alternative_page_links_array: {
          elemMatch: { link: { cached_url: { regex: $productRegex } } }
        }
      }
      sort: { order: DESC, fields: sort_by_date }
    ) @include(if: $isFurnishingCategory) {
      edges {
        node {
          name
          slug
          fullSlug: full_slug
          field_component
          sort_by_date
          shortDescription: field_short_description_string
          images: field_images_array {
            name
            src: filename
          }
          price: field_price_string
          thumbnail: field_thumbnail_object {
            name
            src: filename
          }
          field_color_string
          field_material_string
          field_brand_string
          ...Options
        }
      }
    }
    allCategories: allStoryblokEntry(
      filter: { field_component: { eq: "furnishing_category" } }
    ) @include(if: $isFurnishingCategory) {
      edges {
        node {
          id
          name
          full_slug
        }
      }
    }
    product: storyblokEntry(full_slug: { regex: $originalPath })
      @include(if: $isFurnishingProduct) {
      name
      slug
      full_slug
      field_component
      images: field_images_array {
        name
        filename
      }
      price: field_price_string
      shortDescription: field_short_description_string
      full_slug
      thumbnail: field_thumbnail_object {
        name
        src: filename
      }
      field_address_string
      brand: field_brand_string
      delivery: field_delivery_string
      description: field_description_string
      specs: field_specs_array {
        title
        text
      }
      specsImage: field_specs_image_object {
        filename
        name
      }
      ...Options
    }
    relatedFurnishings: allStoryblokEntry(
      limit: 6
      filter: {
        field_component: { eq: "furnishing" }
        full_slug: { regex: $languageRegex }
      }
    ) @include(if: $isFurnishingProduct) {
      edges {
        node {
          name
          slug
          fullSlug: full_slug
          field_component
          shortDescription: field_short_description_string
          images: field_images_array {
            name
            src: filename
          }
          price: field_price_string
          thumbnail: field_thumbnail_object {
            name
            src: filename
          }
          ...Options
        }
      }
    }
  }
`;

export default StoryblokEntry;
